import { useUpdateFilter } from '../../hooks/useUpdateFilter'
import { useToasts } from '@/hooks/useToasts'
import { useTranslation } from 'react-i18next'

import type { ApolloError } from '@apollo/client'

import type { ColorScheme } from '@hivewatch/dark'
import { Box, Modal } from '@hivewatch/dark'

import { MODAL_Z_INDEX } from '@/styles/theme'

import { IncidentFiltersFormInputsI } from '../../types/form'
import { SavedFilterI } from '../../types/generic'

interface DarkUpdateFilterConfirmationModalIProps {
  isOpen: boolean
  onClose: () => void
  incidentFiltersFormValues: IncidentFiltersFormInputsI
  /** Non-nullable by contract: the caller only renders this modal in UPDATE
   * mode with a filter selected, so the mutation can never fire with an
   * empty id. */
  editedFilter: SavedFilterI
  colorScheme: ColorScheme
}

export const DarkUpdateFilterConfirmationModal = ({
  isOpen,
  onClose,
  incidentFiltersFormValues,
  editedFilter,
  colorScheme,
}: DarkUpdateFilterConfirmationModalIProps) => {
  const { t } = useTranslation()
  const { showSuccess, showError } = useToasts()
  const { update, isUpdateLoading } = useUpdateFilter()

  const handleSave = async () => {
    try {
      await update(editedFilter.id, incidentFiltersFormValues)
      showSuccess(t('incidents:filter_update_success'))
      onClose()
    } catch (error) {
      showError(error as ApolloError)
    }
  }

  return (
    <Modal
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveLoading={isUpdateLoading}
      onClose={onClose}
      onSave={handleSave}
      saveButtonLabel={t('incidents:filter_save')}
      showCancelButton
      size='compact'
      title={t('incidents:filter_update_title')}
      zIndex={MODAL_Z_INDEX}
    >
      <Box>
        {t('incidents:filter_update_body')}{' '}
        <Box as='span' fontWeight='bold'>
          {editedFilter.name}
        </Box>
        ?
      </Box>
    </Modal>
  )
}
